import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('hr_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Employees API
export const employeesApi = {
  getUnprocessed: async () => {
    const response = await axios.get(`${API}/employees/unprocessed`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getProcessed: async () => {
    const response = await axios.get(`${API}/employees/processed`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getById: async (id) => {
    const response = await axios.get(`${API}/employees/${id}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  update: async (id, data) => {
    const response = await axios.put(`${API}/employees/${id}`, data, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  create: async (data) => {
    const response = await axios.post(`${API}/employees`, data, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  submit: async (id, data) => {
    const response = await axios.post(`${API}/employees/${id}/submit`, data, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  unprocess: async (id) => {
    const response = await axios.post(`${API}/employees/${id}/unprocess`, {}, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  // Record locking
  getLocks: async () => {
    const response = await axios.get(`${API}/employees/locks`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  lock: async (id) => {
    const response = await axios.post(`${API}/employees/${id}/lock`, {}, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  unlock: async (id) => {
    const response = await axios.delete(`${API}/employees/${id}/lock`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  unlockAllMine: async () => {
    const response = await axios.post(`${API}/employees/unlock-all-mine`, {}, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  // Admin lock management
  getAdminLocks: async () => {
    const response = await axios.get(`${API}/employees/locks/admin`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  adminUnlock: async (id) => {
    const response = await axios.delete(`${API}/employees/${id}/lock/admin`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  // Delete unprocessed employee (admin only)
  delete: async (id) => {
    const response = await axios.delete(`${API}/employees/${id}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  // Get failed submissions
  getFailedSubmissions: async () => {
    const response = await axios.get(`${API}/employees/failed-submissions`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  // Get pending submissions
  getPendingSubmissions: async () => {
    const response = await axios.get(`${API}/employees/pending-submissions`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  // Retry failed submission
  retrySubmission: async (id) => {
    const response = await axios.post(`${API}/employees/${id}/retry-submission`, {}, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  // Get submission-locked records (admin)
  getSubmissionLocked: async () => {
    const response = await axios.get(`${API}/employees/submission-locked`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  // Force unlock submission (admin)
  forceUnlockSubmission: async (id) => {
    const response = await axios.post(`${API}/employees/${id}/force-unlock-submission`, {}, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  // Re-send record to TherapyQuest (admin)
  resendTherapyQuest: async (id) => {
    const response = await axios.post(`${API}/admin/employees/${id}/resend-therapyquest`, {}, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  // Preview the exact TherapyQuest payload (admin)
  previewTherapyQuest: async (id) => {
    const response = await axios.get(`${API}/admin/therapyquest/preview/${id}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },
};

// Users API (Admin)
export const usersApi = {
  getAll: async () => {
    const response = await axios.get(`${API}/admin/users`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  create: async (email, role) => {
    const response = await axios.post(`${API}/admin/users?email=${encodeURIComponent(email)}&role=${role}`, {}, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  update: async (id, data) => {
    const params = new URLSearchParams();
    if (data.role) params.append('role', data.role);
    if (data.is_active !== undefined) params.append('is_active', data.is_active);
    
    const response = await axios.put(`${API}/admin/users/${id}?${params.toString()}`, {}, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  delete: async (id) => {
    const response = await axios.delete(`${API}/admin/users/${id}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },
};

// Dropdown Lists API
export const listsApi = {
  getAll: async () => {
    const response = await axios.get(`${API}/lists`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  get: async (name) => {
    const response = await axios.get(`${API}/lists/${name}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  update: async (name, items) => {
    const response = await axios.put(`${API}/lists/${name}`, items, {
      headers: getAuthHeader(),
      params: { items: items.join(',') },
    });
    return response.data;
  },

  addItem: async (name, item) => {
    const response = await axios.post(`${API}/lists/${name}/items?item=${encodeURIComponent(item)}`, {}, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  removeItem: async (name, item) => {
    const response = await axios.delete(`${API}/lists/${name}/items/${encodeURIComponent(item)}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },
};

// Analytics API
export const analyticsApi = {
  getOverview: async () => {
    const response = await axios.get(`${API}/analytics/overview`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getMyStats: async () => {
    const response = await axios.get(`${API}/analytics/my-stats`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getProductivity: async (startDate, endDate) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await axios.get(`${API}/analytics/productivity`, {
      headers: getAuthHeader(),
      params,
    });
    return response.data;
  },

  downloadReportPdf: async (startDate, endDate) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await axios.get(`${API}/analytics/report-pdf`, {
      headers: getAuthHeader(),
      params,
      responseType: 'blob',
    });
    return response.data;
  },
};

// Entra ID API (Admin only)
export const entraApi = {
  searchUser: async (query) => {
    const response = await axios.get(`${API}/admin/entra/user/search`, {
      headers: getAuthHeader(),
      params: { query },
    });
    return response.data;
  },

  updateUser: async (userId, data) => {
    const response = await axios.patch(`${API}/admin/entra/user/${userId}`, data, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getAuditLog: async (limit = 100) => {
    const response = await axios.get(`${API}/admin/entra/audit-log`, {
      headers: getAuthHeader(),
      params: { limit },
    });
    return response.data;
  },

  getGroupMembers: async (groupId) => {
    const response = await axios.get(`${API}/entra/group/${groupId}/members`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getFacilityContact: async (facilityCode) => {
    const response = await axios.get(`${API}/entra/facility-contact`, {
      headers: getAuthHeader(),
      params: { facility_code: facilityCode },
    });
    return response.data;
  },

  getAllUsers: async () => {
    const response = await axios.get(`${API}/entra/users/all`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },
};

// Notification API (Admin only)
export const notificationsApi = {
  getRecipients: async () => {
    const response = await axios.get(`${API}/admin/notifications/recipients`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  updateRecipients: async (data) => {
    const response = await axios.put(`${API}/admin/notifications/recipients`, data, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getTemplates: async () => {
    const response = await axios.get(`${API}/admin/notifications/templates`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  updateTemplate: async (templateType, data) => {
    const response = await axios.put(`${API}/admin/notifications/templates/${templateType}`, data, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  sendTest: async () => {
    const response = await axios.post(`${API}/admin/notifications/test`, {}, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  diagnose: async () => {
    const response = await axios.get(`${API}/admin/notifications/diagnose`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },
};


// What's New / Announcements API
export const whatsNewApi = {
  list: async () => {
    const r = await axios.get(`${API}/whats-new`, { headers: getAuthHeader() });
    return r.data;
  },
  unreadCount: async () => {
    const r = await axios.get(`${API}/whats-new/unread-count`, { headers: getAuthHeader() });
    return r.data;
  },
  markRead: async (id) => {
    const r = await axios.post(`${API}/whats-new/${id}/read`, {}, { headers: getAuthHeader() });
    return r.data;
  },
  markAllRead: async () => {
    const r = await axios.post(`${API}/whats-new/mark-all-read`, {}, { headers: getAuthHeader() });
    return r.data;
  },
  // Admin
  adminListAll: async () => {
    const r = await axios.get(`${API}/whats-new/admin/all`, { headers: getAuthHeader() });
    return r.data;
  },
  adminCreate: async (payload) => {
    const r = await axios.post(`${API}/whats-new/admin`, payload, { headers: getAuthHeader() });
    return r.data;
  },
  adminUpdate: async (id, payload) => {
    const r = await axios.patch(`${API}/whats-new/admin/${id}`, payload, { headers: getAuthHeader() });
    return r.data;
  },
  adminDeactivate: async (id) => {
    const r = await axios.delete(`${API}/whats-new/admin/${id}`, { headers: getAuthHeader() });
    return r.data;
  },
};
